import React, { useRef, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ImageRestriction, CropperRef } from "react-advanced-cropper";
import Modal from "./Modal.tsx";
import { Button } from "./Button.tsx";
import { useHashState } from "../service/useHashState.ts";
import { DefaultCropper } from "./croppers/DefaultCropper/DefaultCropper.tsx";
import { DefaultCropperIcon } from "./icons/DefaultCropperIcon.tsx";
import { SettingsIcon } from "./icons/SettingsIcon.tsx";
import { CroppersWizardSettings } from "./CroppersWizardSettings.tsx";
import "./CroppersWizard.scss";
import { useDebouncedCallback } from "use-debounce";
import { toast } from "react-toastify";

export interface CropperSettings {
  aspectRatio?: number;
  minAspectRatio?: number;
  maxAspectRatio?: number;
  imageRestriction?: ImageRestriction;
  stencilType?: "rectangle" | "circle";
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  scaleImage?: boolean;
  grid?: boolean;
}

export interface CropperDescription {
  key: string;
  info: { name: string; description: string };
  link?: string;
  features: string[];
  settings: string[];
}

export interface CroppersWizardProps {
  images?: any;
  setImages?: any;
  imagesArr?: any;
  setImagesArr?: any;
  fileUploadRefs?: any;
  setFileUploadRefs?: any;
  numberOfImages?: any;
  setNumberOfImages?: any;
  removeSource?: any;
  cropperOpen?: any;
  setCropperOpen?: any;
  max?: any;
  allowCrop?: any;
  minARatio?: any;
  setMinAR?: any;
  maxARatio?: any;
  setMaxAR?: any;
  ar?: any;
  setAR?: any;
  resizable?: any;
  setResizable?: any;
  height?: any;
  setHeight?: any;
  width?: any;
  setWidth?: any;
  top?: any;
  setTop?: any;
  left?: any;
  setLeft?: any;
  hasSettings?: any;
  setHasSettings?: any;
  updateCoordinatesCheck?: any;
  setUpdateCoordinatesCheck?: any;
  handleClose?: any;
  handleError?: (error: any) => void;
  [key: string]: any;
}

export default function CroppersWizard({
  images: files = {},
  setImages: setFiles = () => {},
  imagesArr: imagesArray = [],
  setImagesArr: setImagesArray = () => {},
  fileUploadRefs: uploadRefs = {},
  setFileUploadRefs: setUploadRefs = () => {},
  numberOfImages: totalImages = 0,
  setNumberOfImages: setTotalImages = () => {},
  removeSource: setVideo,
  cropperOpen,
  setCropperOpen = () => {},
  max = 6,
  allowCrop,
  minARatio,
  setMinAR = () => {},
  maxARatio,
  setMaxAR = () => {},
  ar,
  setAR = () => {},
  resizable,
  setResizable = () => {},
  height,
  setHeight = () => {},
  width,
  setWidth = () => {},
  top,
  setTop = () => {},
  left,
  setLeft = () => {},
  hasSettings,
  setHasSettings = () => {},
  updateCoordinatesCheck,
  setUpdateCoordinatesCheck = () => {},
  handleClose = () => {},
  ...props
}: CroppersWizardProps) {
  const location = useLocation();
  const defaultFreeSettings: CropperSettings = {
    aspectRatio: undefined,
    minAspectRatio: undefined,
    maxAspectRatio: undefined,
    grid: true,
    imageRestriction: ImageRestriction.fitArea,
  };

  const [isFirstImage, setIsFirstImage] = useState(true);
  const [settings, setSettings] = useState<CropperSettings>({
    aspectRatio: ar,
    minAspectRatio: minARatio,
    maxAspectRatio: maxARatio,
    grid: true,
    imageRestriction: ImageRestriction.fitArea,
  });

  const [settingsChanged, setSettingsChanged] = useState(false);

  const croppers = [
    {
      key: "default-cropper",
      info: { name: "Default Cropper", description: "The styled default cropped with custom navigation" },
      features: ["Custom Navigation", "Styling"],
      icon: <DefaultCropperIcon />,
      settings: ["aspectRatio", "imageRestriction", "stencil", "size", "scaleImage", "grid"],
    },
  ];

  const cropperRef = useRef<CropperRef>(null);
  const [cropper, setCropper] = useHashState(croppers[0].key, croppers.map((el) => el.key));
  const [src, setSrc] = useState<string | undefined>();
  const [customImage, setCustomImage] = useState(false);

  const loadImage = (event: any) => {
    const { files: eventFiles } = event.target;
    if (eventFiles && eventFiles[0]) {
      if (src && customImage) {
        URL.revokeObjectURL(src);
      }
      const blob = URL.createObjectURL(eventFiles[0]);
      setSrc(blob);
      setCustomImage(true);
      setCropperOpen(true);
      setSettingsChanged(false);
    }
  };

  const [showSettings, setShowSettings] = useState(false);

  const onOpenSettings = () => {
    setShowSettings(true);
  };

  const onCloseSettings = (values: any) => {
    setSettings(values);
    setMinAR(values.minAspectRatio);
    setMaxAR(values.maxAspectRatio);
    setAR(values.aspectRatio);
    setSettingsChanged(true);

    if (cropperRef.current) {
      cropperRef.current.refresh();
      setTimeout(() => {
        if (cropperRef.current) {
          const coords = cropperRef.current.getCoordinates();
          if (coords) {
            updateCoordinates(coords);
            if (typeof crop === 'function') {
              crop(cropperRef.current);
            }
          }
        }
      }, 50);
    }
    setShowSettings(false);
  };

  const updateCoordinates = (values: any) => {
    if (values) {
      setHeight(values.height);
      setWidth(values.width);
      setTop(values.top);
      setLeft(values.left);
    }
  };

  const data = croppers.find((el) => el.key === cropper);

  const { minHeight, minWidth, maxWidth, maxHeight, aspectRatio, maxAspectRatio, minAspectRatio, grid } = settings;

  const stencilProps = {
    aspectRatio: isFirstImage 
      ? aspectRatio 
      : (ar || (width && height ? width / height : undefined) || aspectRatio),
    maxAspectRatio: isFirstImage ? maxAspectRatio : undefined,
    minAspectRatio: isFirstImage ? minAspectRatio : undefined,
    resizable: true,
    movable: true,
    grid,
  };

  const defaultSize = ({ imageSize, visibleArea }: { imageSize: any; visibleArea?: any }) => {
    const currentAspectRatio = isFirstImage
      ? (aspectRatio || imageSize.width / imageSize.height)
      : (ar || (width && height ? width / height : imageSize.width / imageSize.height));

    let newWidth = imageSize.width;
    let newHeight = imageSize.height;

    if (currentAspectRatio) {
      if (newWidth / newHeight > currentAspectRatio) {
        newWidth = newHeight * currentAspectRatio;
      } else {
        newHeight = newWidth / currentAspectRatio;
      }
    }

    return {
      width: newWidth,
      height: newHeight,
    };
  };

  const crop = useDebouncedCallback((cropperInstance: CropperRef) => {
    if (!cropperInstance) return;
    try {
      const imageUrisObject: Record<string, string> = {};
      const canvas = cropperInstance.getCanvas({
        width: width,
        height: height,
        maxWidth: 4096,
        maxHeight: 4096,
        fillColor: '#fff',
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      if (canvas && canvas.toDataURL) {
        const dataUrl = canvas.toDataURL('image/jpeg', 1.0);
        if (dataUrl && dataUrl !== 'data:,') {
          if (currentFileInputIndex.current !== null) {
            imageUrisObject[currentFileInputIndex.current] = dataUrl;
          }
          setFiles((prevFiles: any) => ({ ...prevFiles, ...imageUrisObject }));
        }
      }
    } catch (error) {
      console.error("Error in crop function:", error);
    }
  }, 300);

  const exitCrop = (e?: any) => {
    if (e && e.preventDefault) e.preventDefault();
    let updatedFiles = { ...files };
    if (cropperRef.current) {
      const finalCanvas = cropperRef.current.getCanvas({
        width: width,
        height: height,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });
      if (finalCanvas && finalCanvas.toDataURL) {
        const imageUrisObject: Record<string, string> = {};
        if (currentFileInputIndex.current !== null) {
          imageUrisObject[currentFileInputIndex.current] = finalCanvas.toDataURL('image/jpeg', 1.0);
        }
        updatedFiles = { ...files, ...imageUrisObject };
        setFiles(updatedFiles);
      }
    }

    const currentAspectRatio = settingsChanged ? aspectRatio : (width && height ? width / height : 1);
    if (isFirstImage || settingsChanged) {
      setAR(currentAspectRatio);
      let newValues = {
        aspectRatio: currentAspectRatio,
        minAspectRatio: currentAspectRatio,
        maxAspectRatio: currentAspectRatio,
        grid: true,
        imageRestriction: ImageRestriction.fitArea,
      };
      setSettings(newValues);
      if (isFirstImage) {
        setIsFirstImage(false);
      }
    }

    setCurrentImage(null);
    currentFile.current = null;
    currentFileInputIndex.current = null;
    setHasSettings(false);

    let arr = Object.values(updatedFiles);
    setImagesArray(arr);
    setFiles(arr);
    setUpdateCoordinatesCheck(false);
    setSettingsChanged(false);
    handleClose();
  };

  const [originalFiles, setOriginalFiles] = useState(files);
  const [currentImage, setCurrentImage] = useState<any>(null);
  const currentFile = useRef<any>(null);
  const currentFileInputIndex = useRef<any>(null);

  const handleFileChange = async (e: any, index: any) => {
    try {
      e.preventDefault();
      const maxAllowedImages = max - Object.keys(files || {}).length;
      const _selectedFiles = e.target.files;
      if (!_selectedFiles || _selectedFiles.length === 0) return;
      if (e.target.files.length > maxAllowedImages) {
        if (props.handleError) {
          props.handleError(`You cannot upload more than ${max} ${max > 1 ? "images" : "image"}`);
        } else {
          toast.error(`You cannot upload more than ${max} ${max > 1 ? "images" : "image"}`);
        }
        return;
      }
      const selectedFiles = Array.from(e.target.files);

      const imageURIs = await Promise.all(
        selectedFiles.map((f: any) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const image = document.createElement("img");
              image.onload = () => resolve(reader.result);
              image.src = reader.result as string;
            };
            reader.onerror = (err) => reject(err);
            reader.readAsDataURL(f);
          });
        })
      );

      const imageUrisObject: Record<string, any> = {};
      for (let i = index; i < index + imageURIs.length; i++) {
        imageUrisObject[i] = imageURIs[i - index];
        currentFileInputIndex.current = i;
      }
      if (allowCrop) {
        setCurrentImage(imageUrisObject[index + imageURIs.length - 1]);
        setOriginalFiles({ ...originalFiles, ...imageUrisObject });
        loadImage(e);
      }
      let arr = Object.values(files || {});
      arr.push(imageURIs[0]);
      setFiles(arr);
      setImagesArray(arr);
    } catch (err: any) {
      if (props.handleError) props.handleError(err);
      else toast.error(err?.message || err);
    }
  };

  useEffect(() => {
    const refCount = Math.max(Number(max) || 6, Number(totalImages) || 1);
    const fileUploadRefsCopy: Record<number, any> = {};
    for (let i = 0; i < refCount; i++) {
      fileUploadRefsCopy[i] = uploadRefs?.[i] || React.createRef();
    }
    setUploadRefs(fileUploadRefsCopy);
  }, [totalImages, max]);

  useEffect(() => {
    const count = Array.isArray(files) ? files.length : Object.keys(files || {}).length;
    const newTotal = count < max ? count + 1 : count;
    setTotalImages(newTotal);
    if (count === 0) {
      setIsFirstImage(true);
      setSettings(defaultFreeSettings);
      setHasSettings(true);
    }
  }, [files, max]);

  useEffect(() => {
    if (cropperOpen && cropperRef.current) {
      setTimeout(() => {
        if (cropperRef.current) {
          cropperRef.current.refresh();
        }
      }, 100);
    }
  }, [cropperOpen]);

  const activeInputIndex = Math.max(0, (Number(totalImages) || 1) - 1);
  const activeInputRef = uploadRefs?.[activeInputIndex] || uploadRefs?.[0];

  return (
    <>
      <input
        type="file"
        onChange={(e) => handleFileChange(e, activeInputIndex)}
        ref={activeInputRef}
        style={{ display: "none" }}
        accept="image/*"
      />
      {allowCrop && currentImage ? (
        <Modal isOpen={true}>
          <Modal.Header>Crop Image</Modal.Header>
          <Modal.Body>
            <div className={"croppers-wizard"}>
              <div className="croppers-wizard__body">
                <DefaultCropper
                  key={"default-cropper"}
                  wrapperClassName={"croppers-wizard__cropper"}
                  src={src}
                  onReady={(cropperInstance) => {
                    if (cropperInstance) {
                      const coords = cropperInstance.getCoordinates();
                      if (coords) updateCoordinates(coords);
                      crop(cropperInstance);
                    }
                  }}
                  onInteractionEnd={crop}
                  stencilProps={stencilProps}
                  defaultSize={defaultSize}
                  updateCoordinates={updateCoordinates}
                  updateCoordinatesCheck={true}
                  ref={cropperRef}
                />
                {hasSettings && (
                  <button className="croppers-wizard__settings-button" onClick={onOpenSettings}>
                    <SettingsIcon />
                  </button>
                )}
                {data && (
                  <CroppersWizardSettings
                    open={showSettings && hasSettings}
                    settings={settings}
                    onClose={onCloseSettings}
                    properties={data.settings}
                    className={"croppers-wizard__settings"}
                    visibleClassName={"croppers-wizard__settings--visible"}
                  />
                )}
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button type="button" onClick={exitCrop} size="normal">
              Save
            </Button>
          </Modal.Footer>
        </Modal>
      ) : (
        ""
      )}
    </>
  );
}